import React, { useEffect, useRef } from 'react'
import { ButtonLink, Checkbox, Notification, Subheading, SubmitButton } from '@offerzen/pinata-shame'
import { arrayOf, bool, shape, string, func } from 'prop-types'
import { useForm } from 'react-hook-form'
import CityMultiSelect from '../../../CandidateExperience/components/CityMultiSelect'
import ControlledHookField from '../../../CandidateExperience/components/ControlledHookField'
import styles from './styles'
import { formatPreferredLocations } from '../../../CandidateExperience/helpers/candidateHelpers'
import { emptyWorkplacePolicyDefaultValues } from '../../../CandidateExperience/helpers/workplacePolicyHelpers'

const validators = {
  requiredArray: value => (value?.length ? undefined : 'Please select at least 1 city'),
}

const WorkplacePolicyForm = ({
  countries,
  toggleEditing,
  submitWorkplacePolicy,
  isSaving,
  defaultValues = emptyWorkplacePolicyDefaultValues,
  workplacePolicySubmitError,
  locationOsmStrings,
}) => {
  const hybridPreferredLocations = useRef([])
  const officePreferredLocations = useRef([])

  const { control, getValues, watch, handleSubmit, formState, setError, clearErrors } = useForm({
    mode: 'onBlur',
    defaultValues,
  })
  watch()
  const values = getValues()

  useEffect(() => {
    const selectedHybridCities = values.cities.hybrid

    const { selectedPreferredLocations: selectedHybridPreferredLocations } = formatPreferredLocations({
      cities: selectedHybridCities,
      locationOsmStrings,
    })

    hybridPreferredLocations.current = selectedHybridPreferredLocations
  }, [values.cities.hybrid, locationOsmStrings])

  useEffect(() => {
    const selectedOfficeCities = values.cities.office

    const { selectedPreferredLocations: selectedOfficePreferredLocations } = formatPreferredLocations({
      cities: selectedOfficeCities,
      locationOsmStrings,
    })

    officePreferredLocations.current = selectedOfficePreferredLocations
  }, [values.cities.office, locationOsmStrings])

  const validate = () => {
    if (!getValues('policies.hybrid') && !getValues('policies.office') && !getValues('policies.remote')) {
      setError('formError', {
        message: 'Please select at least 1 workplace policy',
      })
      return true
    }
    return false
  }

  const submitForm = formValues => {
    const isError = validate()
    if (!isError) {
      const formValuesWithComprehensiveLocations = {
        ...formValues,
        comprehensiveLocations: {
          office: officePreferredLocations.current,
          hybrid: hybridPreferredLocations.current,
        },
      }
      submitWorkplacePolicy(formValuesWithComprehensiveLocations)
    }
  }

  const handleCancel = () => {
    toggleEditing()
  }

  return (
    <form onSubmit={handleSubmit(submitForm)}>
      <Subheading className={styles.heading}>How would you like to work?</Subheading>
      <ControlledHookField
        onClick={() => clearErrors('formError')}
        className={styles.checkbox}
        control={control}
        id="remote-policy"
        name="policies.remote"
        label="100% Remote"
        component={Checkbox}
      />

      <ControlledHookField
        onClick={() => clearErrors(['formError', 'cities.hybrid'])}
        className={styles.checkbox}
        control={control}
        id="hybrid-policy"
        name="policies.hybrid"
        label="Hybrid"
        component={Checkbox}
      />
      <If condition={values.policies.hybrid}>
        <ControlledHookField
          onClick={() => clearErrors()}
          className={styles.select}
          control={control}
          component={CityMultiSelect}
          countries={countries}
          id="hybrid-cities"
          placeholder="- select -"
          label="Where should the company be based for hybrid work?"
          name="cities.hybrid"
          validate={[validators.requiredArray]}
        />
      </If>

      <ControlledHookField
        onClick={() => clearErrors(['formError', 'cities.office'])}
        className={styles.checkbox}
        control={control}
        id="office-policy"
        name="policies.office"
        label="Office-based"
        component={Checkbox}
      />
      <If condition={values.policies.office}>
        <ControlledHookField
          onClick={() => clearErrors()}
          className={styles.select}
          control={control}
          component={CityMultiSelect}
          countries={countries}
          id="office-cities"
          placeholder="- select -"
          label="Where should the company be based for office-based work?"
          name="cities.office"
          validate={[validators.requiredArray]}
        />
      </If>

      <If condition={workplacePolicySubmitError || formState.errors.formError}>
        <Notification theme="error" className={styles.error}>
          {workplacePolicySubmitError || formState.errors.formError.message}
        </Notification>
      </If>

      <div className={styles.formFooter}>
        <ButtonLink label="Cancel" theme="light" onClick={handleCancel} disabled={isSaving} className={styles.button} />
        <SubmitButton
          label="Save"
          aria-label="Save location and remote preferences"
          isSubmitting={isSaving}
          submittingLabel="Saving"
          className={styles.button}
        />
      </div>
    </form>
  )
}

WorkplacePolicyForm.propTypes = {
  countries: arrayOf(
    shape({
      id: string,
      name: string,
      code: string,
      cities: arrayOf(
        shape({
          id: string,
          name: string,
        }),
      ),
    }),
  ),
  toggleEditing: func,
  defaultValues: shape({
    hybrid: bool,
    remote: bool,
    office: bool,
    hybridCities: arrayOf(string),
    officeCities: arrayOf(string),
  }),
  submitWorkplacePolicy: func,
  isSaving: bool,
  workplacePolicySubmitError: string,
  locationOsmStrings: shape({}),
}

export default WorkplacePolicyForm
